import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { camelCase, startCase, upperFirst } from 'lodash';
import { map } from 'rxjs/operators';
import { Observable, zip } from 'rxjs';

import { environment } from '@env/environment';
import { Menu } from '@app/home/home.component';
import {PrimeNGConfig } from 'primeng/api';


const LOCALE_ID = `${environment.prefix}_locale_id`;
/**
 * A class that provides the user's profile informations such as language, formatting options...etc
 *
 * This class should not be modified directly, you can provide your own service which should extend the default one as follows:
 ```
  export class MyTranslatorService extends TranslatorService {
   ...
  }
 ```
 * And provide your class in the `core/custom-core.module.ts` file as follows:
 ```
  providers: [
   { provide: TranslatorService, useClass: MyTranslatorService }
  ]
 ```
 * */
@Injectable({
  providedIn: 'root'
})
export class TranslatorService {
  MENU_ITEMS = 'Menu Items.';
  LABELS = 'Labels.';
  constructor(private translate: TranslateService,private config: PrimeNGConfig) {}

  /**
   * Capitalizes the provided `word`
   * @param word
   * @returns {string}
   */
  private capitalizeWord(word: string): string {
    return startCase(camelCase(word));
  }
  /**
   * Translates the fields
   * @param modelName
   * @param fieldName
   * @returns {string}
   */
  translateField(modelName, fieldName) {
    if (fieldName) {
      this.translate
        .get('Definitions.' + modelName + '.' + fieldName)
        .subscribe(res => {
          fieldName = !res.includes('Definitions.')
            ? res
            : fieldName;
        });
      return fieldName;
    }
    return '';
  }
    /**
   * Translate Row Operations
   * @param labels
   * @param defaultValue
   */
     translateRowOperation(featureName,screenName,rowOperationName,defaultLabel) {

      if(!!defaultLabel) {
        this.translate
        .get(`Columns.${featureName}.${screenName}.actions.${rowOperationName}`)
        .subscribe((res: string) => {
          if (!res.includes('Columns.')) {
            defaultLabel = res;
          }

        });
      return this.capitalizeWord(defaultLabel);
      }
     return rowOperationName;

    }
  /**
   * Translates the main menu's entries
   * @param menu
   */
  translateMenuItem(menu: Menu[]) {
    menu.forEach(item => {
      // translate item
      this.translate
        .get(this.MENU_ITEMS + item.label)
        .subscribe((res: string) => {
          if (!res.includes(this.MENU_ITEMS)) {
            item.label = res;
          }
        });

      // translate sub items
      if (item.items) {
        this.translateMenuItem(item.items);
      }
    });
  }

 /**
   * Translates labels
   * @param labels
   * @param defaultValue
   */
  getLabels(value: any, defaultValue? : any) {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        // translate item
        this.translate.get(this.LABELS + item.label).subscribe((res: string) => {
          if (!res.includes(this.LABELS)) {
            item.label = res;
          }
        });
      });
    } else {
      this.translate.get(this.LABELS + value).subscribe((res: string) => {
        value = res.includes(this.LABELS) ? defaultValue : res;
      });
    }
     return value
  }
  /**
   * Translates the column's name
   * @param columnLabel
   * @returns {string}
   */
  translateColumnName(columnLabel: string): string {
    this.translate
      .get('Class Fields.' + columnLabel)
      .subscribe((res: string) => {
        if (!res.includes('Class Fields.')) {
          columnLabel = res;
        } else {
          columnLabel = this.capitalizeWord(columnLabel);
        }
      });
    return columnLabel;
  }
  /**
   * Translates the messages
   * @param message
   * @returns {Observable}
   */
  getMessage(message: string, args?: any) {
    return this.translate
      .get('Messages.' + message, args|| null)
      .pipe(map((res: string) => (!res.includes('Messages.') ? res : message)));
    }
   /**
   * Translates the enumeration values
   * @param value
   * @param enumerationName
   * @returns {string}
   */
  translateEnumItem(value: any, enumerationName: any): string{ 
    if (value) {
      this.translate
        .get("Enumeration." + enumerationName + "." + value)
        .subscribe(res => {
          value = !res.includes('Enumeration.')
            ? res
            : value;
        });
      return value;
    }
    return '';
  }
  /**
   * Tanslates the possible value
   * @param enumPossibleValuesList
   * @param enumerationName
   * @param item
   */
  translateEnumItems(
    fieldsPossibleValuesList: Item[],
    enumerationName: string,
    item: string
  ) {
    this.translate
      .get('Enumeration.' + enumerationName + '.' + item)
      .subscribe(res => {
        const translateItem = !res.includes('Enumeration.')
          ? res
          : upperFirst(item);
        fieldsPossibleValuesList.push({ label: translateItem, value: item });
      });
  }
  /**
   * Loads the enumeration's possible values
   * @param enumPossibleValuesList
   * @param enumeration
   * @param enumName
   * @returns {array}
   */
  loadEnumeration(enumPossibleValues, enumeration, enumName) {
    return Object.values(enumeration)
      .filter(item => typeof item === 'string')
      .map(item =>
        this.translateEnumItems(enumPossibleValues, enumName, item.toString())
      );
  }
  /**
   * Loads the enumeration's possible values
   * @param enumeration
   * @param enumName
   * @returns {array}
   */
  enumValues(enumeration, enumName): Observable<any> {
    return zip(
      ...Object.values(enumeration)
        .filter(item => typeof item === 'string')
        .map(item => {
          return this.translate
            .get('Enumeration.' + enumName + '.' + item)
            .pipe(
              map(res => {
                const translateItem = !res.includes('Enumeration.')
                  ? res
                  : upperFirst(item.toString());
                return {
                  label: upperFirst(translateItem),
                  value: item,
                };
                
              })
            );
        })
    );
  }
   /**
   * Loads the enumeration's possible values
   * @param enumeration
   * @param enumName
   * @returns {array}
   */
  enumValuesSorted(enumeration, enumName): Observable<any> {
    return zip(
      ...Object.values(enumeration)
        .filter(item => typeof item === 'string').sort()
        .map(item => {
          return this.translate
            .get('Enumeration.' + enumName + '.' + item)
            .pipe(
              map(res => {
                const translateItem = !res.includes('Enumeration.')
                  ? res
                  : upperFirst(item.toString());
                return {
                  label: upperFirst(translateItem),
                  value: item,
                };
              })
            );
        })
    );
  }
  /**
   * Loads the boolean possible values
   * @returns {array}
   */
   booleanValues(values, enumerationName) {
    let booleanValues = [];
    values
      .forEach(item => {
        this.translate
          .get('Enumeration.' + enumerationName + '.' + item.label)
          .subscribe((res: string) => {
            if (!res.includes('Enumeration.')) {
              item.label = res;
            }
            booleanValues.push(item)
          });
      })

    return booleanValues;
  }
  /**
   * Loads the boolean possible values
   * @param fieldsPossibleValuesList
   * @returns {array}
   */
  loadBoolean(values) {
    return ['true', 'false'].map(item => {
      this.translateEnumItems(values, 'Boolean', item);
    });
  }
  /**
   * Transforms the possible values to a simple list
   * @param possibleValuesList
   * @return {array}
   */
  transformPossibleValueList(possibleValuesList) {
    let ret;
    if (possibleValuesList) {
      ret = possibleValuesList
        .filter(item => typeof item === 'number' || typeof item === 'string')
        .map(
          item =>
          (item = {
            label: item,
            value: item,
          })
        );
    }
    return ret && ret.length > 0 ? ret : possibleValuesList;
  }
  /**
   * Inits the user 'slanguage
   */
  initLanguage() {
    let lang :string;
    if(!localStorage.getItem(LOCALE_ID)){
      lang='pt'
    }else {
      lang = localStorage.getItem(LOCALE_ID) || document.documentElement.lang || 'pt';
    }
    this.translate.use(lang);
    this.translate.get('Primeng').subscribe(res => this.config.setTranslation(res));
    localStorage.setItem(LOCALE_ID, lang);
  }

  /**
   * Translate confirm box with key added to messages in i18n 
   */
  translateConfirmBox(key: string):string{
    let popUp: string
    this.getMessage(key)
    .subscribe(val => {
      popUp = val
    })
    return popUp;
  }
}
/**
 * Menu item interface
 */
export interface Item {
  /**
   * The label property
   */
  label: string;
  /**
   * The value property
   */
  value: string;
}
